/**
 * The parallax backdrop — docs/01-architecture.md §6.2 layer 1.
 *
 * `BiomeDef.parallax` has declared a `{far, mid, near}` triple for all twelve
 * biomes since the roster was written, and `biomes.ts` says in its own header
 * that "the renderer reads its parallax". Nothing read it. This is the reader:
 * the twelve authored triples were, until now, another entry on docs/13 §3's
 * list of complete-and-unreachable systems.
 *
 * WHAT IT IS. One full-screen quad, one draw call, no textures of its own and
 * no assets. Everything on screen is an analytic function of world position and
 * the world seed, in the same spirit as the terrain shader's per-cell grain: the
 * project generates 54 enemies from composition data and every body from a
 * skeleton, and a background that needed a PNG would be the first authored image
 * in it and the first load path in the renderer.
 *
 * THREE LAYERS PLUS A BASE, above and below ground.
 *
 *   factor  above the ground line       below it
 *   ------  -------------------------   ------------------------------------
 *   base    sky gradient, horizon→zenith  the darkest of the biome's triple
 *   0.10    distant ridgeline (hills)     large rock masses, ~86-cell scale
 *   0.25    ragged tree-line              mid-scale pitting, ~29-cell scale
 *   0.45    discrete trees, trunk+canopy  near speckle and a per-cell dither
 *
 * Above ground each layer is *darker* than what is behind it, so it reads as a
 * silhouette against sky. Below ground each layer is *lighter* than the base, so
 * it reads as texture on a wall rather than as holes in one. That inversion is
 * the whole reason a single shader can serve both.
 *
 * WHY THE PARALLAX IS ANCHORED AT THE SURFACE LINE. The naive formula
 * `q = w - (1-p)·cam` puts every layer's ground line at world y = SURFACE_Y only
 * when the camera is at y = 0. At the spawn camera (y ≈ -52) the far hills' base
 * floats 35 cells above the terrain horizon, which looks exactly like a bug.
 * Anchoring the subtraction at SURFACE_Y makes all three layers agree with the
 * world at the horizon and diverge as you leave it, which is what parallax is
 * supposed to look like.
 *
 * HOW IT MEETS THE LIGHT FIELD. The backdrop multiplies by the same `uAmbient`
 * the terrain shader does, so a dark biome's background is dark by the same
 * factor as the rock in front of it and the contrast between them is preserved.
 * It also picks up incident light at half gain, but only underground — a torch
 * should pick out the cave wall behind you, and should not light the sky. Get
 * this wrong in either direction and the world reads as a lit diorama pasted on
 * a flat backdrop, which is the failure this comment exists to name.
 *
 * DETERMINISM. Every function here is pure in (position, seed, phase). There is
 * no *clock*: the clouds do not drift, because a background that moves on its
 * own is a background you look at. `tests/backdrop.test.ts` asserts the absence
 * of a clock at the source, the way `tests/sim.test.ts` does for the simulation.
 *
 * THE ONE THING THAT DOES MOVE, since the paragraph above used to say nothing
 * did: the day/night cycle (#54). It arrives as a single scalar `phase`
 * argument to `render`, computed by the caller from the **simulation tick
 * counter** — see `src/render/dayNight.ts`, which owns the whole of it and
 * explains at length why it is cosmetic, what it deliberately does not do
 * underground, and why it cannot read a clock. Everything it touches is a
 * uniform's *value*; the shader below gains one scalar (`uNight`, for the star
 * field) and no notion of time at all. `phase` defaults to `NOON_PHASE`, at
 * which the tint is exactly the identity, so a caller that never supplies one
 * draws precisely the image this file drew before the cycle existed.
 */

import { profiler } from '../core/profiler';
import { MAT } from '../sim/materialData';
import { materialDef } from '../sim/materials';
import { MOUNTAIN, crest } from '../world/mountain';
import { SURFACE_Y } from '../world/worldgen';
import type { Camera } from './camera';
import {
  NOON_PHASE,
  STAR_DENSITY,
  STAR_TINT,
  nightness,
  tintPalette,
} from './dayNight';
import { GLContext, Uniforms, createProgram } from './gl';
import { createUnlitTexture, type LightTexture } from './light';

// ---------------------------------------------------------------- constants

/**
 * Parallax factors, far to near. A factor is "how much of the camera's motion
 * this layer performs": 0 is welded to the screen, 1 is welded to the world.
 *
 * Chosen low. The brief is depth cueing behind a pixel simulation, not scenery,
 * and above about 0.6 a background layer starts to compete with the terrain for
 * the eye's attention because it is moving at nearly foreground speed.
 */
export const BACKDROP_FACTORS = { far: 0.1, mid: 0.25, near: 0.45 } as const;

/**
 * Where the sky starts giving way, and over how many cells.
 *
 * The first cut of this used the nominal ground line and a 220-cell span,
 * reasoning that a long fade is a smooth one. Looked at in the browser it was
 * plainly a bug: standing 160 cells down, a full treeline was visible through
 * every carved cave near the top of the screen. Those caves are sealed rock;
 * the backdrop cannot know that, so the only defence is for the sky to have
 * ended by then.
 *
 * The top is 12 cells above `SURFACE_Y` because the authored relief puts the
 * actual ground line a little above the nominal one near spawn, and the span is
 * 100 — about a third of a 270-unit screen, which is a visible gradient rather
 * than a line, and fully cave by 60 cells down. `undergroundBlend` is a
 * smoothstep over that window, so the steepest one-cell change anywhere is
 * 1.5/100.
 */
export const BACKDROP_FADE_TOP = SURFACE_Y - 12;
export const BACKDROP_FADE_SPAN = 100;

/**
 * The near layer's trees, in cells. The shader samples one column either side.
 *
 * `spacing` is 22, not the 34 this was first written with. At 34 the trees are
 * big enough to be the subject of the screenshot, which is the one thing the
 * backdrop must not be. A treeline is made of many small trees, and the rest of
 * this table carries the same argument through: a canopy is at most 9 cells in
 * radius, so two neighbours cannot merge into an unbroken wall, and a tree tops
 * out at 40 cells in front of a tree-line whose own crest starts at 26.
 *
 * ONE COPY. These numbers, and `RIDGE` below, used to exist twice — here and
 * again as literals inside the fragment shader — and the two copies disagreed
 * from the day the file was written to the day this comment was added. The
 * shader is now *generated* from these tables the way it has always been
 * generated from `BACKDROP_FACTORS`, so there is no second copy to drift.
 */
export const TREE = {
  spacing: 22,
  /** Clearance kept at each end of the column, so trunks never touch. */
  margin: 4,
  heightBase: 20,
  heightAmp: 20,
  radiusBase: 5,
  radiusAmp: 4,
  trunkBase: 1,
  trunkAmp: 1.5,
} as const;

/** The two octaves' weights and offset. Shared by both ridge layers. */
const OCTAVE = { w0: 0.62, w1: 0.38, ratio: 3.1, offset: 17, octaveSalt: 0x2b } as const;

export interface RidgeParams {
  /** Cycles per cell: the smaller, the wider the landform. */
  freq: number;
  /** Cells between the layer's lowest and highest crest. */
  amp: number;
  /** Cells from the ground line to the layer's lowest crest. */
  base: number;
  /** Decorrelates the layers, so the tree-line is not a scaled copy of the hills. */
  salt: number;
  /** Octave weights, second-octave frequency multiple, offset and salt. */
  w0: number;
  w1: number;
  ratio: number;
  offset: number;
  octaveSalt: number;
}

/**
 * The far hills and the mid tree-line, indexed by layer. See `TREE` on why
 * these live in exactly one place.
 *
 * Layer 1's frequency is about **three times** layer 0's, which is what makes
 * it read as a mass of canopy rather than as a second range of hills, and its
 * band (26..56) overlaps layer 0's (30..64) rather than sitting under it. The
 * overlap is not decoration: the two ridges are painted back to front and each
 * one covers everything below its own crest, so a tree-line that never clears
 * the hills is a parallax layer that is drawn and never seen.
 */
export const RIDGE: readonly [RidgeParams, RidgeParams] = [
  { ...OCTAVE, freq: 0.0055, amp: 34, base: 30, salt: 0x51 },
  { ...OCTAVE, freq: 0.0175, amp: 30, base: 26, salt: 0x9d },
];

/** Zenith and horizon targets the biome's own colours are lifted toward. */
const ZENITH = { r: 0.26, g: 0.31, b: 0.4 };
const HORIZON = { r: 0.52, g: 0.53, b: 0.5 };

// ------------------------------------------------------------- pure functions
//
// Everything below is mirrored line-for-line by the fragment shader further
// down. It lives in TypeScript because a GPU is not available in the test
// runner and "the parallax offset is right" is arithmetic, not an image.
//
// "Mirrored" was a claim this file made and did not keep. `ridgeAt` and
// `treeColumn` were written twice, once here and once as GLSL literals, with
// different numbers in each copy, and stayed that way from the first commit:
// the far hills on screen were up to 8 cells shorter than the ones under test,
// the tree-line ran at a different frequency, and the near trees were half the
// size the exported function reported. Nothing looked broken, which is the
// point — that is the shape a shader regression takes when the test is
// examining a different implementation.
//
// So the word is now enforced rather than promised. The numbers live once, in
// `RIDGE` and `TREE` above, and **the shader is generated from them** the way
// it has always been generated from `BACKDROP_FACTORS`. Where a shape has no
// TypeScript to be generated from — the canopy ellipses, the cave noise, the
// sky gradient — it is still a transcription, and still only as good as the
// next person reading both.

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface BackdropPalette {
  /** The biome's three authored depths, unpacked. Used below the ground line. */
  far: Rgb;
  mid: Rgb;
  near: Rgb;
  /** Derived: the sky at the top of the screen and at the horizon. */
  skyHigh: Rgb;
  skyLow: Rgb;
  /**
   * Derived: the three silhouette colours used above the ground line.
   *
   * Underground, nearer is *lighter* — it is texture catching what light there
   * is. Above ground the ramp runs the other way: nearer is *darker*, because
   * everything above the horizon is a silhouette and distance means haze. Both
   * ramps come from the same authored triple; only the direction differs, and
   * getting the direction wrong flattens the three layers into one mass.
   */
  silFar: Rgb;
  silMid: Rgb;
  silNear: Rgb;
}

/**
 * World coordinate → layer coordinate.
 *
 * `anchor` is the point at which the layer and the world agree. Pass
 * `SURFACE_Y` for the vertical axis so every layer's ground line coincides with
 * the terrain horizon; the horizontal anchor only sets a phase, so it is 0.
 */
export function parallaxCoord(
  world: number,
  camera: number,
  factor: number,
  anchor = 0,
): number {
  return world - (1 - factor) * (camera - anchor);
}

/** 0 = sky, 1 = cave wall. Smooth by construction; see BACKDROP_FADE_SPAN. */
export function undergroundBlend(worldY: number): number {
  const t = (worldY - BACKDROP_FADE_TOP) / BACKDROP_FADE_SPAN;
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}

/**
 * How underground a fragment is, given where it is *and* where the viewer is.
 *
 * The depth test has to be answered twice because one answer is not enough.
 * Ask only the fragment and a screen taken 160 cells down shows a full treeline
 * through the sealed caves at the top of it, because those cells really are
 * above the nominal ground line — the backdrop has no way to know the rock in
 * front of them is solid. Ask only the camera and the surface screen has no
 * horizon at all: everything above and below the ground line gets the same
 * answer. Taking the max means sky survives only where the fragment is above
 * ground and the viewer is still near it, which is exactly when there is sky.
 *
 * `forceDeep` is the prologue and the test rooms: hand-built chambers whose
 * ceilings sit above `SURFACE_Y` and which have no sky by construction.
 */
export function backdropDeep(worldY: number, cameraY: number, forceDeep = false): number {
  if (forceDeep) return 1;
  const a = undergroundBlend(worldY);
  const b = undergroundBlend(cameraY);
  return a > b ? a : b;
}

/** `hash2f` from core/rng.ts, restated so the GLSL transcription is adjacent. */
function h2(x: number, y: number, seed: number): number {
  let h = (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ seed) >>> 0;
  h = (h ^ (h >>> 15)) >>> 0;
  h = Math.imul(h, 0x2c1b3c6d) >>> 0;
  h = (h ^ (h >>> 12)) >>> 0;
  h = Math.imul(h, 0x297a2d39) >>> 0;
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
}

/** Smoothed 1-D value noise. Two hashes; the whole skyline is built from it. */
function vn1(x: number, seed: number): number {
  const f = Math.floor(x);
  let t = x - f;
  t = t * t * (3 - 2 * t);
  const a = h2(f, 0, seed);
  const b = h2(f + 1, 0, seed);
  return a + (b - a) * t;
}

/**
 * Height of a ridge above the ground line, in cells, for an arbitrary layer.
 *
 * Two octaves — one octave is a sine wave and three is indistinguishable from
 * two at this amplitude. Split out from `ridgeAt` so a caller holding a
 * `RidgeParams` it did not get from `RIDGE` can evaluate it: that caller is
 * `tests/backdrop.test.ts`, which recovers the parameters the *shader* was
 * generated with and runs them through this same arithmetic. Comparing two
 * parameter tables would only prove the tables match; comparing the images
 * they produce is what proves the shader draws the function under test.
 */
export function ridgeWith(p: RidgeParams, qx: number, seed: number): number {
  const s = seed ^ p.salt;
  const n =
    vn1(qx * p.freq, s) * p.w0 +
    vn1(qx * p.freq * p.ratio + p.offset, s ^ p.octaveSalt) * p.w1;
  return p.base + p.amp * n;
}

/**
 * Height of a ridge layer above the ground line, in cells.
 *
 * Layer 0 is the far hills: low frequency, tall, smooth. Layer 1 is the mid
 * tree-line: three times the frequency, so it reads as a mass of canopy rather
 * than as a second range of hills, and standing in a band that overlaps the
 * hills' so it is actually visible in front of them.
 */
export function ridgeAt(layer: 0 | 1, qx: number, seed: number): number {
  return ridgeWith(RIDGE[layer], qx, seed);
}

export interface TreeColumn {
  /** Cells per column. Constant; returned so callers cannot disagree with it. */
  spacing: number;
  /** Trunk centre, in layer coordinates. Always inside this column. */
  centre: number;
  /** Cells from the ground line to the top of the canopy. */
  height: number;
  /** Canopy radius. Kept under `spacing` so a 3-column sample cannot clip it. */
  radius: number;
  /** Trunk half-width. */
  trunk: number;
}

/**
 * The one tree in a near-layer column.
 *
 * One per column rather than a Poisson scatter because the shader has to answer
 * "is this fragment inside a tree" by sampling a *bounded* neighbourhood, and a
 * scatter has no bound. The tree is confined to its own column and its canopy is
 * narrower than the spacing, so three columns is provably enough.
 */
export function treeColumn(column: number, seed: number): TreeColumn {
  const a = h2(column, 11, seed);
  const b = h2(column, 12, seed);
  const c = h2(column, 13, seed);
  return {
    spacing: TREE.spacing,
    centre: column * TREE.spacing + TREE.margin + a * (TREE.spacing - 2 * TREE.margin),
    height: TREE.heightBase + b * TREE.heightAmp,
    radius: TREE.radiusBase + c * TREE.radiusAmp,
    trunk: TREE.trunkBase + c * TREE.trunkAmp,
  };
}

/**
 * The east skyline — the desert past the mountain, seen at last.
 *
 * `docs/27` §13.6 recorded the limit honestly: in a side view, "the other side
 * of a gigantic mountain" is behind 1 076 cells of rock, so #140's *"visible,
 * unreachable, promised"* was shipped as *drawn, unreachable, promised* — and
 * named the parallax layer as the mechanism that would pay the difference.
 * This is that payment: east of the mountain, the backdrop's skyline is the
 * desert's — flat-topped mesas in sandstone haze instead of hills and canopy.
 *
 * **The blend is keyed to the fragment's world x, and the band is the
 * mountain's own flank.** From the cave lip to the crest's east edge the
 * mountain is 500+ cells of authored rock in front of the backdrop, so the
 * forest-to-desert seam lives exactly where nothing can see it: west of the
 * lip the sky is all forest, the crest's east edge is all desert, and the
 * transition is only ever on screen for a body above the summit — where a haze
 * blend is what distance looks like anyway.
 *
 * **Derived from `mountain.ts`, not restated.** A tread added to the staircase
 * moves the crest and takes this seam along; a literal here would strand the
 * desert sky over the forest, which is `desertY()`'s argument verbatim.
 */
export const EAST_SKYLINE = {
  x0: MOUNTAIN.caveLipX,
  x1: crest().x1,
} as const;

/** 0 = forest skyline, 1 = desert skyline. Smoothstep over the flank. */
export function eastSkylineBlend(worldX: number): number {
  const t = (worldX - EAST_SKYLINE.x0) / (EAST_SKYLINE.x1 - EAST_SKYLINE.x0);
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}

export interface MesaParams {
  /** Cells per plateau: one height decision per span. */
  span: number;
  /** Distinct plateau heights. Quantisation is what makes a mesa a mesa. */
  levels: number;
  /** Cells between the lowest and highest plateau top. */
  amp: number;
  /** Cells from the ground line to the lowest plateau top. */
  base: number;
  /** Decorrelates the two layers, same as `RidgeParams.salt`. */
  salt: number;
}

/**
 * The two mesa layers, far and mid, indexed like `RIDGE`.
 *
 * A mesa profile is the *opposite* construction from a ridge: a ridge is
 * smooth noise and reads as forest hills; a mesa is one height decision per
 * plateau, held flat for the whole span and dropped vertically at the edge.
 * The far layer's plateaus are half again the mid's, and the two bands overlap
 * the way `RIDGE`'s do and for the same reason — a butte that never clears the
 * far mesas is a layer that is drawn and never seen.
 *
 * ONE COPY: the shader is generated from this table, `TREE`'s rule.
 */
export const MESA: readonly [MesaParams, MesaParams] = [
  { span: 96, levels: 4, amp: 30, base: 26, salt: 0x67 },
  { span: 44, levels: 3, amp: 24, base: 18, salt: 0xb3 },
];

/**
 * Height of a mesa above the ground line for an arbitrary parameter set.
 *
 * Split out from `mesaAt` for `ridgeWith`'s reason verbatim: the caller is
 * `tests/backdrop.test.ts`, which recovers the parameters the *shader* was
 * generated with and runs them through this same arithmetic — comparing the
 * images the two produce rather than the tables they were built from.
 */
export function mesaWith(p: MesaParams, qx: number, seed: number): number {
  const n = h2(Math.floor(qx / p.span), 0, seed ^ p.salt);
  return p.base + (p.amp * Math.floor(n * p.levels)) / (p.levels - 1);
}

/**
 * Height of a mesa layer above the ground line, in cells.
 *
 * Piecewise constant by construction: every `qx` in one span shares one hash
 * and one quantised level, so the top is flat for `span` cells and the change
 * between neighbours is a vertical cliff. `floor(n * levels)` lands on
 * `0..levels-1` because the hash is strictly below 1.
 */
export function mesaAt(layer: 0 | 1, qx: number, seed: number): number {
  return mesaWith(MESA[layer], qx, seed);
}

/**
 * The desert's three silhouettes, in the same air as the biome's sky.
 *
 * The base colour is `SANDSTONE`, **read from the material table** rather than
 * restated — the skyline past the crest is a promise about the ground you will
 * stand on, and a literal here would let the two drift apart, which is the
 * design-system audit's hand-copy defect wearing a sunset. The chain is then
 * `backdropPalette`'s own: darken, haze toward the horizon, and run the result
 * through `tintPalette` so noon is the identity and midnight dims the mesas by
 * exactly the factor it dims the canopy.
 */
export function desertSilhouettes(
  base: BackdropPalette,
  phase: number,
): { silFar: Rgb; silMid: Rgb; silNear: Rgb } {
  const sc = materialDef(MAT.SANDSTONE).color;
  const stone = { r: sc.r / 255, g: sc.g / 255, b: sc.b / 255 };
  const silNear = mixRgb(scale(stone, 0.62), base.skyLow, 0.08);
  const t = tintPalette(
    {
      ...base,
      silNear,
      silMid: mixRgb(silNear, base.skyLow, 0.15),
      silFar: mixRgb(silNear, base.skyLow, 0.3),
    },
    phase,
  );
  return { silFar: t.silFar, silMid: t.silMid, silNear: t.silNear };
}

/**
 * Biome triple → the five colours the shader needs.
 *
 * The three depths are the biome's own authored bytes, verbatim: they are
 * already spaced against the readability rim by `tests/materials.test.ts`, and
 * re-deriving them here would put twelve authored identities behind one
 * renderer's opinion. Only the sky is invented, and it is invented *from* the
 * triple — a lift toward a neutral zenith and a paler horizon — so the Cinder
 * March's sky is not the Rotwood's.
 */
export function backdropPalette(parallax: {
  far: number;
  mid: number;
  near: number;
}): BackdropPalette {
  const far = unpack(parallax.far);
  const mid = unpack(parallax.mid);
  const near = unpack(parallax.near);
  const skyHigh = mixRgb(scale(far, 2.6), ZENITH, 0.45);
  const skyLow = mixRgb(scale(mid, 2.8), HORIZON, 0.5);
  // Deliberately not `far * 0.5`, which is what this was first written as: the
  // authored triples are already very dark, so half of `far` is functionally
  // black and the near trees came out as hard black lollipops in front of a
  // lit brown surface — the loudest thing on screen. A slight darkening plus a
  // touch of haze keeps them reading as *part of* the same air as the sky.
  const silNear = mixRgb(scale(far, 0.85), skyLow, 0.08);
  return {
    far,
    mid,
    near,
    skyHigh,
    skyLow,
    silNear,
    silMid: mixRgb(silNear, skyLow, 0.15),
    silFar: mixRgb(silNear, skyLow, 0.3),
  };
}

/**
 * Depth layers to draw at a given LOD pressure. **0 means skip the pass.**
 *
 * docs/01 §6.4 fixes the degradation order — decorative particles, then smoke
 * resolution, then light resolution, then parallax — so this sheds late, and
 * terrain and entities are not in the ladder at all.
 *
 * Measured at 2560×1440 (3.69 Mpix, dpr 2) on the machine this was written on,
 * best of six runs of 200 draws each, GPU-synced with a 1-pixel readPixels:
 *
 *   layers   surface   transition   deep
 *   3        0.122     0.118        0.097   ms
 *   2        0.099     0.106        0.093
 *   1        0.090     0.095        0.082
 *   0        —         —            —       (not drawn)
 *
 * The interesting row is the one that is not there. A full-screen quad whose
 * fragment shader does nothing but sample one texture already costs 0.08 ms at
 * this resolution, so **two thirds of the pass is fill, not arithmetic** and
 * dropping layers one at a time buys almost nothing. That is why the bottom
 * rung skips the draw outright rather than simplifying it: at maximum pressure
 * the backdrop is gone and the clear colour shows through, which is exactly the
 * image the game had before this file existed. A ladder whose steps do not
 * change the cost is decoration, and docs/13 §6 is about not shipping those.
 */
export function backdropLayerCount(lodPressure: number): number {
  if (lodPressure >= 0.95) return 0;   // skipped entirely
  if (lodPressure >= 0.9) return 1;
  if (lodPressure >= 0.7) return 2;
  return 3;
}

function unpack(hex: number): Rgb {
  return {
    r: ((hex >> 16) & 255) / 255,
    g: ((hex >> 8) & 255) / 255,
    b: (hex & 255) / 255,
  };
}

function scale(c: Rgb, k: number): Rgb {
  return { r: Math.min(1, c.r * k), g: Math.min(1, c.g * k), b: Math.min(1, c.b * k) };
}

function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

// -------------------------------------------------------------------- shaders
//
// The two helpers below are what make "the shader is a transcription" a
// mechanical fact rather than a promise. GLSL will not take `34` where a float
// is expected — `float amp = layer == 0 ? 34 : 30.0;` does not compile — so a
// number cannot simply be dropped into the template; `glf` is the reason the
// constants above can be written as plain numbers and still reach the GPU.

/** A number as a GLSL float literal: 34 becomes `34.0`, 0.0055 stays put. */
const glf = (n: number): string => (Number.isInteger(n) ? n.toFixed(1) : String(n));

/** A number as a GLSL hex int literal, matching how the salts are written above. */
const glx = (n: number): string => `0x${n.toString(16)}`;

const VERT = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
uniform vec2 uCamera;
uniform vec2 uViewSize;
out vec2 vWorld;
void main() {
  // Mirror of terrain.ts's mapping: +y is down in world space, so the sign
  // flips on the way back out of clip space.
  vWorld = uCamera + vec2(aPos.x, -aPos.y) * uViewSize * 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;

in vec2 vWorld;
out vec4 outColor;

uniform vec2 uCamera;
uniform vec2 uAnchor;        // (0, SURFACE_Y)
uniform vec3 uFar;
uniform vec3 uMid;
uniform vec3 uNear;
uniform vec3 uSkyHigh;
uniform vec3 uSkyLow;
uniform vec3 uSilFar;
uniform vec3 uSilMid;
uniform vec3 uSilNear;
// The desert's three silhouettes, east of the mountain. Derived in
// desertSilhouettes() from SANDSTONE and the biome's own horizon, so the mesas
// sit in the same air as the forest they replace.
uniform vec3 uSilFarD;
uniform vec3 uSilMidD;
uniform vec3 uSilNearD;
uniform float uSeed;
uniform float uFadeTop;
uniform float uFadeSpan;
uniform float uCamDeep;   // undergroundBlend(cameraY) — see backdropDeep
uniform float uForceDeep;    // 1 where there is no sky at all (the prologue)
uniform float uLayers;       // 3, 2 or 1 — LOD, docs/01 §6.4
// 1 - daylight, from the cycle phase. NOT a clock and NOT a frame counter: the
// caller derives it from the simulation tick, see render/dayNight.ts. It exists
// only so the star field knows whether to be there; every other part of the
// cycle arrives already baked into the colour uniforms above.
uniform float uNight;

uniform sampler2D uLight;
uniform vec2 uLightOrigin;
uniform vec2 uLightScale;
uniform float uAmbient;
uniform float uLightGain;

const float FAR  = ${BACKDROP_FACTORS.far};
const float MID  = ${BACKDROP_FACTORS.mid};
const float NEAR = ${BACKDROP_FACTORS.near};
const float SP   = ${glf(TREE.spacing)};
// EAST_SKYLINE, derived from mountain.ts at generation time — the seam rides
// the crest the way desertY() rides the tread table.
const float EAST_X0 = ${glf(EAST_SKYLINE.x0)};
const float EAST_X1 = ${glf(EAST_SKYLINE.x1)};
// dayNight.ts::starMask, transcribed. The reciprocal is precomputed because the
// tail test runs once per sky fragment.
const float STAR_CUT = ${(1 - STAR_DENSITY).toFixed(6)};
const float STAR_INV = ${(1 / STAR_DENSITY).toFixed(1)};
const vec3  STAR_RGB = vec3(${STAR_TINT.r}, ${STAR_TINT.g}, ${STAR_TINT.b});

// core/rng.ts hash2, in uint arithmetic so it matches Math.imul exactly.
uint ihash(int x, int y, int s) {
  uint h = (uint(x) * 0x27d4eb2du) ^ (uint(y) * 0x165667b1u) ^ uint(s);
  h ^= h >> 15u; h *= 0x2c1b3c6du;
  h ^= h >> 12u; h *= 0x297a2d39u;
  h ^= h >> 15u;
  return h;
}
float h2(int x, int y, int s) { return float(ihash(x, y, s)) * (1.0 / 4294967296.0); }

float vn1(float x, int s) {
  float f = floor(x);
  float t = x - f;
  t = t * t * (3.0 - 2.0 * t);
  return mix(h2(int(f), 0, s), h2(int(f) + 1, 0, s), t);
}

float vn2(vec2 p, int s) {
  vec2 f = floor(p);
  vec2 t = p - f;
  t = t * t * (3.0 - 2.0 * t);
  int i = int(f.x);
  int j = int(f.y);
  return mix(
    mix(h2(i, j, s),     h2(i + 1, j, s),     t.x),
    mix(h2(i, j + 1, s), h2(i + 1, j + 1, s), t.x),
    t.y);
}

// ridgeAt / ridgeWith, generated from RIDGE. Every number on the next five
// lines is interpolated: there is no second copy of the skyline to disagree
// with the one the tests exercise, which is the state this file was in for its
// whole life before #56.
float ridgeAt(int layer, float qx, int s) {
  float freq = layer == 0 ? ${glf(RIDGE[0].freq)} : ${glf(RIDGE[1].freq)};
  float amp  = layer == 0 ? ${glf(RIDGE[0].amp)} : ${glf(RIDGE[1].amp)};
  float base = layer == 0 ? ${glf(RIDGE[0].base)} : ${glf(RIDGE[1].base)};
  int   sd   = s ^ (layer == 0 ? ${glx(RIDGE[0].salt)} : ${glx(RIDGE[1].salt)});
  float n = vn1(qx * freq, sd) * ${glf(RIDGE[0].w0)} + vn1(qx * freq * ${glf(RIDGE[0].ratio)} + ${glf(RIDGE[0].offset)}, sd ^ ${glx(RIDGE[0].octaveSalt)}) * ${glf(RIDGE[0].w1)};
  return base + amp * n;
}

// mesaAt, generated from MESA. One height decision per plateau span, held
// flat and quantised: the same one-copy rule as ridgeAt above.
float mesaAt(int layer, float qx, int s) {
  float span = layer == 0 ? ${glf(MESA[0].span)} : ${glf(MESA[1].span)};
  float lv   = layer == 0 ? ${glf(MESA[0].levels)} : ${glf(MESA[1].levels)};
  float amp  = layer == 0 ? ${glf(MESA[0].amp)} : ${glf(MESA[1].amp)};
  float base = layer == 0 ? ${glf(MESA[0].base)} : ${glf(MESA[1].base)};
  int   sd   = s ^ (layer == 0 ? ${glx(MESA[0].salt)} : ${glx(MESA[1].salt)});
  float n = h2(int(floor(qx / span)), 0, sd);
  return base + amp * floor(n * lv) / (lv - 1.0);
}

vec2 layerCoord(vec2 world, float p) {
  return world - (1.0 - p) * (uCamera - uAnchor);
}

// ------------------------------------------------------------------- above

vec3 skyAndTrees(vec2 world, int seed) {
  vec2 qf = layerCoord(world, FAR);

  // Vertical gradient, keyed to the far layer's own ground line so the horizon
  // sits where the far hills stand rather than at a fixed screen height.
  float up = clamp((uAnchor.y - qf.y) / 300.0, 0.0, 1.0);
  float ups = up * up * (3.0 - 2.0 * up);
  vec3 col = mix(uSkyLow, uSkyHigh, ups);

  // One band of very low-contrast cloud. Amplitude is 4%: enough that the sky
  // is not a flat wash, far too little to look at.
  float cloud = vn1(qf.x * 0.0042 + 91.0, seed ^ 0x1d);
  col *= 1.0 + (cloud - 0.5) * 0.08 * (1.0 - up);

  // Stars — dayNight.ts::starMask. Additive, before the silhouettes, so hills
  // and canopy occlude them for free. Faded by ups, i.e. out at the horizon:
  // that is where haze eats them in a real sky, and it is also where the near
  // trees are, so the field never competes with the skyline. The branch is on a
  // uniform, so at noon it is a coherent no-op across the whole screen.
  if (uNight > 0.004) {
    float sp = h2(int(floor(qf.x)), int(floor(qf.y)), seed ^ 0x7c);
    float st = (sp - STAR_CUT) * STAR_INV;
    if (st > 0.0) col += STAR_RGB * (0.2 + 0.8 * st * st) * uNight * ups;
  }

  // The east skyline: 0 = forest, 1 = desert. Keyed to the fragment's world x
  // so the seam is a fixed place, and that place is inside the mountain's own
  // bulk — see EAST_SKYLINE at the top of the file. Both scenes are computed
  // only inside the blend band, which is on screen only above the summit.
  float db = smoothstep(EAST_X0, EAST_X1, world.x);

  vec3 west = col;
  if (db <= 0.999) {
    // Far hills. The most hazed of the three silhouettes: distance is the only
    // depth cue a flat silhouette has, and haze is what carries it.
    float topFar = floor(uAnchor.y - ridgeAt(0, floor(qf.x), seed));
    if (qf.y >= topFar) west = uSilFar;

    if (uLayers >= 2.0) {
      vec2 qm = layerCoord(world, MID);
      float topMid = floor(uAnchor.y - ridgeAt(1, floor(qm.x), seed));
      if (qm.y >= topMid) west = uSilMid;
    }

    if (uLayers >= 3.0) {
      vec2 qn = layerCoord(world, NEAR);
      float ground = uAnchor.y;
      bool hit = qn.y >= ground;             // the near layer's own earth
      if (!hit) {
        // Per-cell jitter on the canopy edge. A clean ellipse reads as a lollipop
        // at any size; a boundary that wobbles by a fifth of a radius, quantised
        // to the cell grid, reads as foliage and costs one hash.
        float ragged = 0.78 + 0.44 * h2(int(floor(qn.x)), int(floor(qn.y)), seed ^ 0x4f);
        int c0 = int(floor(qn.x / SP));
        for (int k = -1; k <= 1; k++) {
          int c = c0 + k;
          float a = h2(c, 11, seed);
          float b = h2(c, 12, seed);
          float d = h2(c, 13, seed);
          // treeColumn, generated from TREE. Same rule as ridgeAt above: these
          // four lines are the exported function, not a copy of it.
          float centre = float(c) * SP + ${glf(TREE.margin)} + a * (SP - ${glf(2 * TREE.margin)});
          float height = ${glf(TREE.heightBase)} + b * ${glf(TREE.heightAmp)};
          float radius = ${glf(TREE.radiusBase)} + d * ${glf(TREE.radiusAmp)};
          // Trunk width scales with the tree. A fixed 2-cell stick under a
          // 20-cell crown is a mushroom at every zoom level.
          if (abs(qn.x - centre) <= ${glf(TREE.trunkBase)} + d * ${glf(TREE.trunkAmp)} && qn.y >= ground - height) hit = true;
          // Two overlapping crowns, the upper one offset. One symmetric ellipse
          // is a lollipop no matter how it is proportioned; the asymmetry is what
          // makes twenty of them read as twenty different trees.
          vec2 e = vec2((qn.x - centre) / (radius * 1.15),
                        (qn.y - (ground - height + radius * 0.85)) / (radius * 0.70));
          if (dot(e, e) <= ragged) hit = true;
          vec2 e2 = vec2((qn.x - centre - (a - 0.5) * radius * 1.4) / (radius * 0.85),
                         (qn.y - (ground - height + radius * 1.6)) / (radius * 0.55));
          if (dot(e2, e2) <= ragged) hit = true;
        }
      }
      if (hit) west = uSilNear;
    }
  }

  vec3 east = col;
  if (db >= 0.001) {
    // Far mesas, then mid buttes — flat tops, vertical cliffs, no trees. A
    // desert skyline is what it withholds: the emptiness is the promise.
    float topFarD = floor(uAnchor.y - mesaAt(0, floor(qf.x), seed));
    if (qf.y >= topFarD) east = uSilFarD;

    if (uLayers >= 2.0) {
      vec2 qm = layerCoord(world, MID);
      float topMidD = floor(uAnchor.y - mesaAt(1, floor(qm.x), seed));
      if (qm.y >= topMidD) east = uSilMidD;
    }

    if (uLayers >= 3.0) {
      vec2 qn = layerCoord(world, NEAR);
      if (qn.y >= uAnchor.y) east = uSilNearD;   // bare ground, nothing on it
    }
  }

  return mix(west, east, db);
}

// ------------------------------------------------------------------- below

vec3 caveWall(vec2 world, int seed) {
  // The base is darker than any authored depth: the wall behind the wall.
  vec3 col = uFar * 0.55;

  vec2 q0 = layerCoord(world, FAR);
  float n0 = vn2(q0 * (1.0 / 86.0), seed ^ 0x31);
  col = mix(col, uFar, smoothstep(0.32, 0.78, n0));

  if (uLayers >= 2.0) {
    vec2 q1 = layerCoord(world, MID);
    float n1 = vn2(q1 * (1.0 / 29.0), seed ^ 0x62);
    col = mix(col, uMid, smoothstep(0.52, 0.90, n1) * 0.55);
  }

  if (uLayers >= 3.0) {
    // Quantised to whole cells: an unquantised per-cell hash at 0.45 parallax
    // crawls against the terrain grid and reads as shimmer rather than grain.
    vec2 q2 = floor(layerCoord(world, NEAR));
    float n2 = vn2(q2 * (1.0 / 11.0), seed ^ 0xa7);
    col = mix(col, uNear, smoothstep(0.62, 0.95, n2) * 0.30);
    col *= 0.94 + 0.12 * h2(int(q2.x), int(q2.y), seed ^ 0xd3);
  }
  return col;
}

void main() {
  int seed = int(uSeed);

  // backdropDeep in GLSL: the fragment's own depth, floored by the camera's.
  float t = clamp((vWorld.y - uFadeTop) / uFadeSpan, 0.0, 1.0);
  float deep = max(max(t * t * (3.0 - 2.0 * t), uCamDeep), uForceDeep);

  // Branch rather than blend. The two halves are coherent across almost the
  // whole screen; only the fade band pays for both, and it is one band.
  vec3 col;
  if (deep <= 0.001) {
    col = skyAndTrees(vWorld, seed);
  } else if (deep >= 0.999) {
    col = caveWall(vWorld, seed);
  } else {
    col = mix(skyAndTrees(vWorld, seed), caveWall(vWorld, seed), deep);
  }

  // Same form as the terrain shader, so the background dims by exactly the
  // factor the rock in front of it does. Incident light is admitted only
  // underground and at half gain: a fire should pick out the cave wall behind
  // you; it should not light the sky.
  vec3 incident = texture(uLight, (vWorld - uLightOrigin) * uLightScale).rgb;
  col *= uAmbient + incident * (uLightGain * 0.5 * deep);

  outColor = vec4(col, 1.0);
}`;

/**
 * The fragment shader as a string, **after** interpolation.
 *
 * Exported for `tests/backdrop.test.ts`, which recovers the ridge and tree
 * constants from it and checks they draw the same skyline the exported
 * functions do. Deliberately the interpolated text and not the file's source:
 * reading the source would only prove a `${'$'}{...}` was typed, and a template
 * hole that has been quietly replaced by a literal is exactly the regression
 * worth catching. This is the byte-for-byte string handed to `createProgram`.
 */
export const BACKDROP_FRAG = FRAG;

// ------------------------------------------------------------------- renderer

/**
 * What the backdrop needs from the world. `WorldGen` satisfies it structurally,
 * which keeps a concrete world class out of `render/` and lets the tests hand it
 * a two-line stub.
 */
export interface BackdropWorld {
  readonly seed: number;
  biomeAt(
    worldX: number,
    worldY: number,
  ): { parallax: { far: number; mid: number; near: number } };
}

/** Fallback for scenes with no worldgen — the prologue chamber and test rooms. */
const DEFAULT_PARALLAX = { far: 0x121a12, mid: 0x1d2a18, near: 0x2e3d20 };

export class BackdropRenderer {
  private glc: GLContext;
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private uni: Uniforms;
  private unlitTex: WebGLTexture;

  /**
   * The palette actually drawn, eased toward the biome under the camera.
   *
   * `biomeIndexAt` is a hard choice per position, so crossing a band line would
   * otherwise repaint the entire background between two frames. Presentation
   * only — nothing downstream reads it — so a fixed per-frame rate is fine and
   * saves threading `dt` through for a colour fade.
   */
  private current: BackdropPalette | null = null;

  constructor(glc: GLContext) {
    this.glc = glc;
    this.gl = glc.gl;
    this.prog = createProgram(this.gl, VERT, FRAG, 'backdrop');
    this.uni = new Uniforms(this.gl, this.prog);
    this.unlitTex = createUnlitTexture(this.gl);
  }

  /**
   * One quad, drawn before terrain.
   *
   * `world` may be null: the prologue has no worldgen, and its chamber ceiling
   * sits above `SURFACE_Y`, so it would otherwise show sky through the rock. A
   * null world therefore also forces the underground branch.
   *
   * `phase` is the day/night cycle position in [0, 1), which the caller gets
   * from `dayPhase(cells.tick)` — a simulation tick, never a clock. It defaults
   * to `NOON_PHASE`, where `tintPalette` is the identity, so a caller that has
   * not been wired up yet draws exactly what this renderer drew before #54.
   */
  render(
    camera: Camera,
    light: LightTexture | null,
    world: BackdropWorld | null,
    phase: number = NOON_PHASE,
  ): void {
    profiler.begin('render');
    const gl = this.gl;

    // Bottom of the LOD ladder (docs/01 §6.4): draw nothing and let the clear
    // colour stand. Two thirds of this pass is fill, so simplifying the shader
    // would not have been a degradation step worth having.
    const layers = backdropLayerCount(profiler.lodPressure);
    if (layers < 1) {
      profiler.end('render');
      return;
    }

    const target = backdropPalette(
      world === null
        ? DEFAULT_PARALLAX
        : world.biomeAt(camera.renderX, camera.renderY).parallax,
    );
    this.current = this.current === null ? target : easePalette(this.current, target, 0.05);
    // The cycle is applied **after** the biome ease, not before it. `current` is
    // a per-frame low-pass on a hard biome choice, so tinting the target would
    // put the sky twenty frames behind the phase and — worse — make the drawn
    // colour a function of frame history rather than of the tick. Applied here
    // it is a pure function of (eased biome palette, phase), which is what makes
    // "same seed, same tick, same sky" a statement anybody can check.
    const p = tintPalette(this.current, phase);

    gl.useProgram(this.prog);
    gl.bindVertexArray(this.glc.quad());
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    if (light) {
      light.bind(0);
      this.uni.f2('uLightOrigin', light.originX, light.originY);
      this.uni.f2('uLightScale', light.scaleX, light.scaleY);
      this.uni.f1('uAmbient', light.ambient);
      this.uni.f1('uLightGain', light.gain);
    } else {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.unlitTex);
      this.uni.f2('uLightOrigin', 0, 0);
      this.uni.f2('uLightScale', 0, 0);
      this.uni.f1('uAmbient', 1);
      this.uni.f1('uLightGain', 0);
    }
    this.uni.i1('uLight', 0);

    this.uni.f2('uCamera', camera.renderX, camera.renderY);
    this.uni.f2('uViewSize', camera.viewW, camera.viewH);
    this.uni.f2('uAnchor', 0, SURFACE_Y);
    this.uni.f3('uFar', p.far.r, p.far.g, p.far.b);
    this.uni.f3('uMid', p.mid.r, p.mid.g, p.mid.b);
    this.uni.f3('uNear', p.near.r, p.near.g, p.near.b);
    this.uni.f3('uSkyHigh', p.skyHigh.r, p.skyHigh.g, p.skyHigh.b);
    this.uni.f3('uSkyLow', p.skyLow.r, p.skyLow.g, p.skyLow.b);
    this.uni.f3('uSilFar', p.silFar.r, p.silFar.g, p.silFar.b);
    this.uni.f3('uSilMid', p.silMid.r, p.silMid.g, p.silMid.b);
    this.uni.f3('uSilNear', p.silNear.r, p.silNear.g, p.silNear.b);
    // From the eased (untinted) palette, tinted inside: noon is the identity
    // and midnight dims the mesas by the factor it dims the canopy.
    const d = desertSilhouettes(this.current, phase);
    this.uni.f3('uSilFarD', d.silFar.r, d.silFar.g, d.silFar.b);
    this.uni.f3('uSilMidD', d.silMid.r, d.silMid.g, d.silMid.b);
    this.uni.f3('uSilNearD', d.silNear.r, d.silNear.g, d.silNear.b);
    // Masked to 24 bits because `uSeed` is a float32 uniform and `int(uSeed)`
    // would silently drop the low bits of a full 32-bit seed. Truncation would
    // not break determinism — the same seed still yields the same skyline — but
    // it would mean two seeds differing only in their low bits share a horizon,
    // which is a worse kind of "deterministic".
    this.uni.f1('uSeed', world === null ? 0 : world.seed & 0xffffff);
    this.uni.f1('uFadeTop', BACKDROP_FADE_TOP);
    this.uni.f1('uCamDeep', undergroundBlend(camera.renderY));
    this.uni.f1('uFadeSpan', BACKDROP_FADE_SPAN);
    this.uni.f1('uForceDeep', world === null ? 1 : 0);
    this.uni.f1('uLayers', layers);
    this.uni.f1('uNight', nightness(phase));

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    profiler.count('drawCalls');

    gl.bindVertexArray(null);
    gl.enable(gl.BLEND);
    profiler.end('render');
  }
}

function easePalette(a: BackdropPalette, b: BackdropPalette, k: number): BackdropPalette {
  return {
    far: mixRgb(a.far, b.far, k),
    mid: mixRgb(a.mid, b.mid, k),
    near: mixRgb(a.near, b.near, k),
    skyHigh: mixRgb(a.skyHigh, b.skyHigh, k),
    skyLow: mixRgb(a.skyLow, b.skyLow, k),
    silFar: mixRgb(a.silFar, b.silFar, k),
    silMid: mixRgb(a.silMid, b.silMid, k),
    silNear: mixRgb(a.silNear, b.silNear, k),
  };
}
